import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SupabaseAuthService } from '../../supabase/supabase-auth.service';
import { SupabaseDbContestantsService } from '../../supabase/supabase-db-contestants.service';
import { fromApi } from '../../shared/utils/case-convert';
import { establishmentYearRegex, postCodeRegex, urlRegex } from '../../shared/validators/regex.validators';
import { MatStepper } from '@angular/material/stepper';
import { AuthStore } from '../auth.store';
import { ContestantStore } from '../../contestant/contestant.store';
import { Contestant } from '../../shared/models/auth.models';
import { pickFormValues } from '../../shared/utils/forms';
import { filter, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';

interface SignUpForm {
  // Registration Step
  email: FormControl<string | null>,
  password: FormControl<string | null>,
  notifications: FormControl<boolean | null>,
  dataConsent: FormControl<boolean | null>,
  termsConsent: FormControl<boolean | null>,
  // Name step
  gender: FormControl<string | null>,
  namePrefix: FormControl<string | null>,
  firstName: FormControl<string | null>,
  lastName: FormControl<string | null>,
  // Company step
  legalForm: FormControl<string | null>,
  companyName: FormControl<string | null>,
  establishmentYear: FormControl<string | null>,
  category: FormControl<string | null>,
  taxNumber: FormControl<string | null>,
  state: FormControl<string | null>,
  location: FormControl<string | null>,
  address: FormControl<string | null>,
  postCode: FormControl<string | null>,
  phone: FormControl<string | null>,
  website: FormControl<string | null>,
  facebook: FormControl<string | null>,
  instagram: FormControl<string | null>,
}

@Injectable({
  providedIn: 'root',
})
export class SignUpService {
  private readonly supabaseAuthService = inject(SupabaseAuthService);
  private readonly dbContestants = inject(SupabaseDbContestantsService);
  private readonly contestantStore = inject(ContestantStore);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);

  public readonly currentYear = new Date().getFullYear();

  public signUpForm = new FormGroup<SignUpForm>({
    // Registration Step
    email: new FormControl(''),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
    ]),
    notifications: new FormControl(false),
    dataConsent: new FormControl(false, [Validators.requiredTrue]),
    termsConsent: new FormControl(false, [Validators.requiredTrue]),
    // Name step
    gender: new FormControl('', [Validators.required]),
    namePrefix: new FormControl(''),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    // Company step
    legalForm: new FormControl('', [Validators.required]),
    companyName: new FormControl('', [Validators.required]),
    establishmentYear: new FormControl('', [Validators.required, Validators.pattern(establishmentYearRegex)]),
    category: new FormControl('', [Validators.required]),
    taxNumber: new FormControl(''),
    state: new FormControl('', [Validators.required]),
    location: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required]),
    postCode: new FormControl('', [Validators.required, Validators.pattern(postCodeRegex)]),
    phone: new FormControl('', [Validators.required]),
    website: new FormControl('', [Validators.pattern(urlRegex)]),
    facebook: new FormControl('', [Validators.pattern(urlRegex)]),
    instagram: new FormControl('', [Validators.pattern(urlRegex)]),
  });

  public setSignUpForm(
    data: Partial<Record<keyof SignUpForm, any>>,
    options: {
      resetForm?: boolean,
      emitEvent?: boolean,
      onlyFields?: (keyof SignUpForm)[]
    } = {}): void {
    const {resetForm = false, emitEvent = true, onlyFields} = options;

    if (resetForm) {
      this.signUpForm.reset();
    }

    if (onlyFields && onlyFields.length > 0) {
      const filteredData = Object.keys(data)
        .filter(key => onlyFields.includes(key as keyof SignUpForm))
        .reduce((obj, key) => {
          obj[key] = data[key as keyof typeof data];
          return obj;
        }, {} as Record<string, any>);

      this.signUpForm.patchValue(filteredData, {emitEvent});
    } else {
      this.signUpForm.patchValue(data, {emitEvent});
    }
  }

  public readonly stepFields: Record<string, string[]> = {
    registration: [
      'email',
      'password',
      'dataConsent',
      'termsConsent',
      'notifications',
    ],
    name: [
      'gender',
      'firstName',
      'lastName',
      'namePrefix',
    ],
    company: [
      'legalForm',
      'companyName',
      'establishmentYear',
      'category',
      'state',
      'location',
      'address',
      'postCode',
      'phone',
      'taxNumber',
      'website',
      'facebook',
      'instagram',
    ],
  };

  public async checkInitialFormValues(stepper: MatStepper) {
    if (!this.supabaseAuthService.isAuthenticated) return;
    const contestant = await firstValueFrom(
      this.contestantStore.contestant$.pipe(filter(Boolean))
    );
    const {nameStepValid, companyStepValid} = this.parseContestantAndCheckValidity(contestant);
    if (!nameStepValid) {
      stepper.selectedIndex = 2;
    } else if (!companyStepValid) {
      stepper.selectedIndex = 3;
    } else {
      this.router.navigate(['/contestant']);
    }
    return {nameStepValid, companyStepValid};
  }

  public disableFields(groupFields: string, exceptions: string[] = []): void {
    const fields = this.stepFields[groupFields];
    fields.forEach(field => {
      if (!exceptions.includes(field)) {
        const control = this.signUpForm.get(field);
        if (control) {
          control.disable();
        }
      }
    });
  }

  public enableFields(groupFields: string): void {
    const fields = this.stepFields[groupFields];
    fields.forEach(field => {
      const control = this.signUpForm.get(field);
      if (control) {
        control.enable();
      }
    });
  }

  public checkButtonDisabled(step: string): boolean {
    const fields: string[] = this.stepFields[step];
    if (!fields) return false;
    return fields.some((key) => !this.signUpForm.get(key)!.valid);
  }

  public async signUp(): Promise<void> {
    const email: string | undefined = this.signUpForm.controls.email?.value?.trim();
    const password: string | undefined = this.signUpForm.controls.password?.value?.trim();

    if (!email || !password) throw new Error('Ungültige Eingabedaten für die Registrierung.');

    await this.supabaseAuthService.signUp(email, password);
  }

  public async updateUser(stepNumber: number, contestantId?: string): Promise<void> {
    const userId: string | undefined = contestantId ?? this.authStore.user?.id;
    if (!userId) throw new Error('Benutzer nicht angemeldet.');

    let dataToUpdate: any = {};

    if (stepNumber === 1) {
      dataToUpdate = pickFormValues(this.stepFields['registration'], this.signUpForm);
      delete dataToUpdate.email;
      delete dataToUpdate.password;
    } else if (stepNumber === 2) {
      dataToUpdate = pickFormValues(this.stepFields['name'], this.signUpForm);
    } else if (stepNumber === 3) {
      dataToUpdate = pickFormValues(this.stepFields['company'], this.signUpForm);
      dataToUpdate.phone = `${dataToUpdate.phone}`;
    }

    dataToUpdate = {...dataToUpdate, lastWizardStep: stepNumber};

    const {data, error} = await this.dbContestants.update(userId, dataToUpdate);

    if (error) throw error;

    this.contestantStore.contestant = fromApi<Contestant>(data);
    localStorage.removeItem('signUpForm');
  }

  public parseContestantAndCheckValidity(contestant: any) {
    this.signUpForm.patchValue(contestant as any);
    const nameStepControls = this.stepFields['name'].map(f => this.signUpForm.get(f));
    const companyStepControls = this.stepFields['company'].map(f => this.signUpForm.get(f));
    const nameStepValid = nameStepControls.every(control => control?.valid);
    const companyStepValid = companyStepControls.every(control => control?.valid);
    return {nameStepValid, companyStepValid};
  }
}
